// VI-370: optional countdown timer shown for the answer window, plus a 30-second
// "wrap up" warning. Shared by the video (Interview.tsx) and audio
// (AudioInterview.tsx) flows so both stay pixel-identical. The pages already
// track `recordingTime` (0 → 120s); we derive everything visual from that.
//
// Styling mirrors the VI-370 Figma component (node 3050:2760 / overlay 3049:13847):
//   badge  → 50% white, 1px #d1dae8, radius 4px, "MM:SS", Poppins SemiBold 13
//   toggle → icon-only bordered box (text label returns on the new jobseeker
//            pages). Hollow stopwatch either way: #2F86DD off (Show), #8B8E94 on (Hide).

export const MAX_ANSWER_SECONDS = 120;
export const WARNING_THRESHOLD_SECONDS = 30;
export const WARNING_RED = '#DC2626'; // Surface/Error/Danger

// Design tokens pulled from the Figma variable definitions.
const OUTLINE = '#d1dae8'; // Border/Neutrals/Outline
const TEXT_DARK = '#272a2f'; // Text/text-body (countdown digits)
const TEXT_SECONDARY = '#585b62'; // Text/text-secondary (VI-834 labeled toggle text)
const SHOW_TIMER_BLUE = '#2F86DD'; // "Show Timer" icon — inviting/actionable
const HIDE_TIMER_GREY = '#8B8E94'; // "Hide Timer" icon — muted/active
const POPPINS = "'Poppins', sans-serif"; // Foundations/Primary — matches CountdownOverlay

const TIMER_TOOLTIP = 'A countdown timer is displayed indicating time left to answer';

// MM:SS with a leading-zero minute, matching the Figma "02:00" readout.
const formatCountdown = (secondsRemaining: number): string => {
  const clamped = Math.max(0, secondsRemaining);
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

// Hollow (outline) stopwatch — shown when the timer is OFF ("Show Timer").
const StopwatchOutline = ({ size, color }: { size: number; color: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="13.5" r="8" />
    <path d="M9 2.5h6" />
    <path d="M12 2.5v3" />
    <path d="M17.7 7.8l1.3-1.3" />
    <path d="M12 13.5V9" />
  </svg>
);

interface AnswerTimerBadgeProps {
  recordingTime: number; // seconds elapsed since recording started (0 before start)
  isMobile?: boolean;
  isRecording?: boolean; // true only while actually recording (drives the live dot)
}

/**
 * Top-left overlay badge showing time remaining in the 2:00 answer window.
 * Shows a static 02:00 in the "upcoming recording" state (so jobseekers can
 * preview the feature) and counts down once recording starts. When 30s or fewer
 * remain the box BORDER turns red (#DC2626) to prompt wrap-up (AC #7) — the
 * digits stay dark. Meant to sit inside a `position: relative` preview box.
 */
export const AnswerTimerBadge = ({ recordingTime, isMobile, isRecording }: AnswerTimerBadgeProps) => {
  const secondsRemaining = Math.max(0, MAX_ANSWER_SECONDS - recordingTime);
  const isWarning = secondsRemaining <= WARNING_THRESHOLD_SECONDS;

  return (
    <div
      role="timer"
      aria-live="off"
      aria-label={`Time remaining ${formatCountdown(secondsRemaining)}`}
      style={{
        position: 'absolute',
        top: isMobile ? '12px' : '16px',
        left: isMobile ? '12px' : '16px',
        zIndex: 6,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 12px',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        WebkitBackdropFilter: 'blur(6px)',
        backdropFilter: 'blur(6px)',
        borderRadius: '4px',
        border: `1px solid ${isWarning ? WARNING_RED : OUTLINE}`,
        fontFamily: POPPINS,
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: '20px',
        color: TEXT_DARK,
        userSelect: 'none',
        pointerEvents: 'none',
        transition: 'border-color 0.2s ease',
      }}
    >
      {/* Status dot — matches the digit color while previewing the timer
          pre-record, then red once the recording is actually live so it isn't
          mistaken for an active recording. */}
      <span
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: isRecording ? WARNING_RED : TEXT_DARK,
          flexShrink: 0,
        }}
      />
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCountdown(secondsRemaining)}</span>
    </div>
  );
};

interface TimerToggleProps {
  showTimer: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  // VI-834: the redesigned recording pages pass `redesign` → labeled pill on
  // desktop, icon-only on mobile, brand-blue border when OFF on both. Defaults
  // to false → the legacy icon-only box (neutral border) is unchanged.
  redesign?: boolean;
}

/**
 * Show / Hide Timer toggle (AC #2, #4). Icon-only for the current jobseeker
 * pages — the text label returns on the new pages (VI-834, `showLabel`) — sized
 * to sit inline with the recording controls. Carries the GA ids (AC #9) and an
 * accessible label.
 *
 * Tooltip (AC #3) is shown ONLY in the "Show Timer" (off) state and is
 * hover-only. VI-820: the shared @jobtarget/ui-library Tooltip toggled its
 * visibility on focus AND hover via a parity reducer, so a button that kept
 * focus after a click left the tooltip stuck open across Chrome/Edge/Firefox.
 * A pure CSS `:hover` bubble (no focus trigger) can't desync and hides the
 * moment the pointer leaves.
 */
export const TimerToggle = ({ showTimer, onToggle, isMobile, redesign }: TimerToggleProps) => {
  const label = showTimer ? 'Hide Timer' : 'Show Timer';
  const boxSize = isMobile ? 36 : 40;
  // VI-834: labeled pill on the redesigned desktop; icon-only elsewhere.
  const showLabel = !!redesign && !isMobile;
  const iconSize = showLabel ? 20 : (isMobile ? 18 : 20);
  // VI-834 redesign: brand-blue border while OFF (inviting), neutral (#d9dade,
  // the redesign's neutral token) once ON. Legacy keeps the VI-370 OUTLINE.
  const borderColor = redesign ? (showTimer ? '#d9dade' : SHOW_TIMER_BLUE) : OUTLINE;

  const button = (
    <button
      type="button"
      onClick={onToggle}
      id={showTimer ? 'btn-hidetimer' : 'btn-showtimer'}
      aria-pressed={showTimer}
      aria-label={label}
      aria-describedby={showTimer ? undefined : 'vi-timer-tip-desc'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: showLabel ? '8px' : 0,
        width: showLabel ? 'auto' : `${boxSize}px`,
        height: `${boxSize}px`,
        padding: showLabel ? '0 12px' : 0,
        backgroundColor: '#ffffff',
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        cursor: 'pointer',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        transition: 'border-color 0.2s ease',
      }}
    >
      <StopwatchOutline size={iconSize} color={showTimer ? HIDE_TIMER_GREY : SHOW_TIMER_BLUE} />
      {showLabel && (
        <span
          style={{
            fontFamily: POPPINS,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            color: TEXT_SECONDARY,
          }}
        >
          {label}
        </span>
      )}
    </button>
  );

  // Timer is ON → no tooltip (per product decision), just the button.
  if (showTimer) return button;

  return (
    <span className={`vi-timer-tip${redesign ? ' vi-timer-tip--edge' : ''}`}>
      <style>{`
        .vi-timer-tip { position: relative; display: inline-flex; }
        .vi-timer-tip__bubble {
          position: absolute; bottom: calc(100% + 8px); left: 50%;
          transform: translateX(-50%) translateY(4px);
          background: #1f2733; color: #fff; font-size: 12px; line-height: 1.4;
          text-align: center; padding: 6px 10px; border-radius: 8px;
          width: max-content; max-width: 220px; white-space: normal;
          box-shadow: 0 6px 24px rgba(0,0,0,0.28);
          opacity: 0; pointer-events: none;
          transition: opacity .16s ease, transform .16s ease; z-index: 30;
        }
        .vi-timer-tip__bubble::after {
          content: ''; position: absolute; top: 100%; left: 50%;
          transform: translateX(-50%); border: 6px solid transparent; border-top-color: #1f2733;
        }
        .vi-timer-tip:hover .vi-timer-tip__bubble { opacity: 1; transform: translateX(-50%) translateY(0); }
        /* VI-834: on the redesigned pages the toggle sits at the right edge, so the
           bubble is right-anchored (opens leftward) to stay within the viewport. */
        .vi-timer-tip--edge .vi-timer-tip__bubble {
          left: auto; right: 0; transform: translateX(0) translateY(4px);
        }
        .vi-timer-tip--edge:hover .vi-timer-tip__bubble { transform: translateX(0) translateY(0); }
        .vi-timer-tip--edge .vi-timer-tip__bubble::after { left: auto; right: 14px; transform: translateX(0); }
        @media (prefers-reduced-motion: reduce) { .vi-timer-tip__bubble { transition: none; } }
      `}</style>
      {button}
      <span className="vi-timer-tip__bubble" id="vi-timer-tip-desc" role="tooltip">{TIMER_TOOLTIP}</span>
    </span>
  );
};
